import { Stack, Grid } from "@mui/material";
import ErrorPageIllustration from "@/components/ErrorPageIllustration";
import { ROUTE_PARAMS, LINKS } from "@/constants";
import { AccountContext } from "@/context/account/AccountContext";
import { getDealerAccountRouteId } from "@/context/account/dealerAccountRouteId";
import { useSupportMode } from "@/context/supportMode";
import { authSignOutRedirect } from "@/provider/AuthProvider/signOut";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "react-oidc-context";
import { Link, generatePath } from "react-router";
import { Button, Typography } from "@trimble/modus-cmx";

const useDashboardPath = (): string => {
  const accountContext = useContext(AccountContext);
  const { session: supportModeSession } = useSupportMode();

  if (accountContext !== undefined) {
    return generatePath(`/:${ROUTE_PARAMS.ACCOUNT_ID}`, {
      [ROUTE_PARAMS.ACCOUNT_ID]: accountContext.accountRouteId,
    });
  }

  if (supportModeSession !== null) {
    return generatePath(`/:${ROUTE_PARAMS.ACCOUNT_ID}`, {
      [ROUTE_PARAMS.ACCOUNT_ID]: getDealerAccountRouteId({
        id: supportModeSession.cdhId,
        iamId: supportModeSession.iamAccountId,
      }),
    });
  }

  return "/";
};

type GenericErrorTemplateProps = {
  header: string;
  description?: string;
  showDealerDashboardButton?: boolean;
};

export default function GenericErrorTemplate(props: GenericErrorTemplateProps) {
  const { header, description, showDealerDashboardButton = true } = props;

  const { t } = useTranslation();
  const auth = useAuth();
  const dashboardPath = useDashboardPath();

  return (
    <Grid container size="grow" spacing={8} alignItems="center">
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack gap={3}>
          <Stack gap={1}>
            <Typography hierarchy="h2" size="2xl" weight="bold">
              {header}
            </Typography>
            <Typography hierarchy="h4" size="lg">
              {description}
            </Typography>
          </Stack>
          <Stack direction="row" gap={2} flexWrap="wrap">
            {showDealerDashboardButton && (
              <Link to={dashboardPath}>
                <Button>{t("error_template.back_to_dashboard")}</Button>
              </Link>
            )}
            <a href={LINKS.CONTACT_US}>
              <Button variant="outlined" color="neutral">
                {t("error_template.contact_us")}
              </Button>
            </a>
            <Button
              variant="outlined"
              color="neutral"
              onButtonClick={() => {
                void authSignOutRedirect(auth.signoutRedirect);
              }}
            >
              {t("profile_drawer.sign_out")}
            </Button>
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <ErrorPageIllustration alt={t("something_went_wrong")} />
      </Grid>
    </Grid>
  );
}
